import { PageSizing } from 'components/Layout/PageContentWidth';
import { ContentCard, PageWrapper } from '../styles';
import { TFunction, useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import Loader from '__components/Kizen/Loader';
import BillingService from 'services/BillingService';
import LazyLoadList from '__components/Kizen/LazyLoad/LazyLoadList';
import { RowCell, RowWrapper } from './styles';
import { useEndsOfYScroll } from '__components/Kizen/LazyLoad/helpers';
import { colorsPrimary, colorsSecondary, grayScale } from '__app/colors';
import { Dot } from '__components/Fields/ColorLabel';
import Icon from '__components/Kizen/Icon';
import IconButtonMenu from '__components/Kizen/IconButtonMenu';
import { emptyLongDash } from '__components/Inputs/DateTimeInput/helpers';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid':
      return colorsPrimary.green.dark;
    case 'payment_failed':
      return colorsSecondary.red.dark;
    case 'charge_pending':
      return colorsPrimary.orange.dark;
    case 'approved':
      return colorsPrimary.blue.dark;
  }
};

const getStatusLabel = (status: string, t: TFunction) => {
  switch (status) {
    case 'paid':
      return t('Paid');
    case 'payment_failed':
      return t('Payment Failed');
    case 'charge_pending':
      return t('Charge Pending');
    case 'approved':
      return t('Payment Scheduled');
  }
};

export const BillingInvoicesPage = () => {
  const { t } = useTranslation();
  const scrollContainer = useRef<HTMLDivElement>(null);

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['billing', 'invoices'],
    queryFn: async ({ pageParam = 1 }) => {
      const data = await BillingService.getInvoicesPage(pageParam, 20);
      return data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next
        ? parseInt(new URL(lastPage.next).searchParams.get('page') || '1')
        : undefined;
    },
    getPreviousPageParam: (previousPage) => {
      return previousPage?.previous
        ? parseInt(
            new URL(previousPage.previous).searchParams.get('page') || '1'
          )
        : undefined;
    },
    retry: false,
    throwOnError: false,
    enabled: true,
  });

  const onScrollThreshold = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const invoices = useMemo(() => {
    const res =
      invoicesData?.pages.reduce((acc, page) => {
        return acc.concat(page.results);
      }, []) ?? [];

    return res;
  }, [invoicesData]);

  const [scrolledTop, scrolledBottom] = useEndsOfYScroll(scrollContainer, [
    invoices,
  ]);

  const isScrollable = !scrolledTop || !scrolledBottom;

  const chooseOptionHandler = useCallback(
    (invoiceItem: any) =>
      ({ value }: { value: string }) => {
        const { receipt_file } = invoiceItem;

        switch (value) {
          case 'download': {
            const link = document.createElement('a');
            link.href = `${receipt_file}?disposition=attachment`;
            link.download = 'receipt.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            break;
          }
          case 'view': {
            window.open(receipt_file, '_blank');
            break;
          }
        }
      },
    []
  );

  if (invoicesLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      <PageWrapper>
        <div className="w-full">
          <div>
            <ContentCard padding={0}>
              <div className="p-[20px]">
                <Typography variant="header" size="2xl" weight="semibold">
                  {t('Invoices')}
                </Typography>
              </div>
              <div className="w-full h-[600px] pb-[35px]">
                <RowWrapper header useScrollbarPadding={isScrollable}>
                  <RowCell>
                    <Typography weight="semibold">
                      {t('Invoice Date')}
                    </Typography>
                  </RowCell>
                  <RowCell>
                    <Typography weight="semibold">
                      {t('Charge Date')}
                    </Typography>
                  </RowCell>
                  <RowCell>
                    <Typography weight="semibold">{t('Type')}</Typography>
                  </RowCell>
                  <RowCell>
                    <Typography weight="semibold">{t('Status')}</Typography>
                  </RowCell>
                  <RowCell>
                    <Typography weight="semibold">
                      {t('Invoice Total')}
                    </Typography>
                  </RowCell>
                  <RowCell>
                    <Typography weight="semibold">{t('Actions')}</Typography>
                  </RowCell>
                </RowWrapper>
                <LazyLoadList
                  data={invoices}
                  onScrollThreshold={onScrollThreshold}
                  fetchingNextPage={isFetchingNextPage}
                  loading={invoicesLoading}
                  scrollContainerRef={scrollContainer}
                  className="kds-expanding-scrollbar"
                  kdsCompat
                >
                  {invoices.map((invoice: any) => {
                    return (
                      <RowWrapper key={invoice.created}>
                        <RowCell>
                          <Typography truncate>
                            {new Date(invoice.created).toLocaleDateString()}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate>
                            {invoice.paid_at
                              ? new Date(invoice.paid_at).toLocaleDateString()
                              : emptyLongDash}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate>
                            {invoice.invoice_type}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Dot color={getStatusColor(invoice.status)} />
                          <Typography truncate>
                            {getStatusLabel(invoice.status, t)}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate>
                            {invoice.total
                              ? `$${invoice.total}`
                              : emptyLongDash}
                          </Typography>
                        </RowCell>
                        <RowCell right>
                          {invoice.receipt_file ? (
                            <IconButtonMenu
                              title={t('Perform Action')}
                              position="right"
                              sizing="dense"
                              options={[
                                {
                                  value: 'view',
                                  label: t('View Receipt'),
                                },
                                {
                                  value: 'download',
                                  label: t('Download PDF Receipt'),
                                },
                              ]}
                              onChange={chooseOptionHandler(invoice)}
                              restrictCloseHandler
                              color={grayScale.mediumDark}
                            >
                              <Icon icon="three-dot" />
                            </IconButtonMenu>
                          ) : null}
                        </RowCell>
                      </RowWrapper>
                    );
                  })}
                </LazyLoadList>
              </div>
            </ContentCard>
          </div>
        </div>
      </PageWrapper>
    </PageSizing>
  );
};
